import React from 'react';
import {  FiCheckCircle, FiUsers, FiBarChart2, FiSearch, FiGlobe, FiMonitor, FiStar } from 'react-icons/fi';

const AttractStudents = () => {
  const secrets = [
    {
      icon: <FiSearch className="w-6 h-6" />,
      title: 'Speak Your Parents\' Language',
      description: 'Parents search using specific phrases—"best CBSE school in [city]," "affordable degree college near me," "JEE coaching with good results." Use these exact phrases on your website. Don\'t use jargon. Use the words parents actually type into Google.',
      stat: '72% of parents use specific location-based search phrases',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Show Up Where Parents Are Looking',
      description: 'Parents are searching on Google, not Facebook. Your website must appear in search results. This means optimizing for local SEO, creating program-specific pages, and publishing content parents find valuable.',
      stat: '89% of parents start their school search on Google',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiMonitor className="w-6 h-6" />,
      title: 'Make a Great First Impression',
      description: 'When parents land on your website, they decide within seconds whether to stay or leave. A clean, professional, easy-to-navigate website builds trust. A cluttered, slow, confusing website drives them away.',
      stat: '75% of parents judge an institution\'s credibility by its website',
      color: 'bg-amber-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Show Success Stories',
      description: 'Parents want to see results. Share stories of successful students, alumni achievements, and parent testimonials. Real stories build trust and help parents imagine their child succeeding at your institution.',
      stat: '85% of parents are influenced by alumni success stories',
      color: 'bg-rose-50'
    },
    {
      icon: <FiStar className="w-6 h-6" />,
      title: 'Make It Easy to Take Action',
      description: 'Don\'t make parents hunt for information. Make it easy to find admission details, fee structures, program offerings, and contact information. Every page should guide parents toward taking the next step.',
      stat: '68% of parents abandon websites that are hard to navigate',
      color: 'bg-purple-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Track and Improve Continuously',
      description: 'SEO is not a one-time effort. Monitor your website traffic, see which pages attract the most visitors, and continuously improve. Add new content. Update program pages. Respond to parent questions. Keep improving.',
      stat: 'Websites that continuously update content rank 3x higher',
      color: 'bg-blue-50'
    }
  ];

  const rankingFactors = [
    {
      title: 'Relevant Keywords',
      description: 'Use keywords parents actually search for—not just institution jargon. Think like a parent, not an educator.'
    },
    {
      title: 'Local SEO',
      description: 'Include city, locality, and landmark names. Create a Google My Business profile. Get listed in local directories.'
    },
    {
      title: 'Quality Content',
      description: 'Blog posts, program descriptions, admission guides, and FAQ pages. Content that helps parents make decisions.'
    },
    {
      title: 'Mobile-Friendly Design',
      description: 'Most parents search on phones. Your website must look perfect on every device.'
    },
    {
      title: 'Fast Loading Speed',
      description: 'Parents are impatient. If your website loads slowly, they leave and go to a competitor.'
    },
    {
      title: 'Parent Reviews',
      description: 'Positive reviews on Google and other platforms build trust and improve your local SEO ranking.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Every educational institution wants more students. Schools want to fill classrooms. Colleges want to meet admission targets. Coaching centres want to grow their batches. But in a competitive market, attracting students is not easy.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        The secret? <strong className="text-slate-900">A website that actually ranks on Google.</strong> When parents search for schools, colleges, or coaching centres, they find institutions that rank at the top of search results. They don't find institutions that are invisible.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's the secret to attracting more students—and how you can do it too.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">89%</div>
          <div className="text-sm text-slate-500">Parents start their search on Google</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">75%</div>
          <div className="text-sm text-slate-500">Judge credibility by website</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">3x</div>
          <div className="text-sm text-slate-500">Higher ranking with continuous content updates</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">The Secret to Attracting More Students</h2>

      {/* Secrets List */}
      {secrets.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Ranking Factors */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">What Makes a Website Rank on Google</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {rankingFactors.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-2">
                <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Invisible vs. Findable Institution</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Invisible Institution</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>No SEO optimization</span></li>
              <li className="flex items-start gap-2">• <span>Doesn't appear on Google</span></li>
              <li className="flex items-start gap-2">• <span>Parents can't find them</span></li>
              <li className="flex items-start gap-2">• <span>Losing students to competitors</span></li>
              <li className="flex items-start gap-2">• <span>Poor website experience</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Findable Institution</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>SEO-optimized website</span></li>
              <li className="flex items-start gap-2">• <span>Rank #1 on Google</span></li>
              <li className="flex items-start gap-2">• <span>Parents find them easily</span></li>
              <li className="flex items-start gap-2">• <span>Attracting more students</span></li>
              <li className="flex items-start gap-2">• <span>Great website experience</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The Parent's Mind */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Parent's Mind</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          When parents are looking for a school, college, or coaching centre, they are anxious. They want the best for their child. They want to trust the institution they choose. They want to feel confident in their decision.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          A website that ranks on Google signals that your institution is legitimate, credible, and trustworthy. When parents find you easily, they are more likely to consider you. When they have a great experience on your website, they are more likely to choose you.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Your website is your first impression. Make it count.</strong>
        </p>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon Helps You Attract More Students</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔍</div>
            <h4 className="font-bold text-slate-900 text-sm">SEO-Optimized</h4>
            <p className="text-xs text-slate-600">Built-in SEO for better Google ranking</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile-Friendly</h4>
            <p className="text-xs text-slate-600">Perfect on every device</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Fast Loading</h4>
            <p className="text-xs text-slate-600">Optimized for speed</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📝</div>
            <h4 className="font-bold text-slate-900 text-sm">Content Management</h4>
            <p className="text-xs text-slate-600">Easy updates and blog posts</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Analytics</h4>
            <p className="text-xs text-slate-600">Track parent behavior</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔗</div>
            <h4 className="font-bold text-slate-900 text-sm">ERP Integration</h4>
            <p className="text-xs text-slate-600">Website + ERP in one platform</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Attract More Students with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          The secret to attracting more students is simple: be findable. When parents search for institutions like yours, they should find you first. An SEO-optimized website makes this possible.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon builds websites that rank on Google. SEO-optimized, mobile-friendly, fast loading, and easy to update. Attract more students. Grow your institution.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for student attraction.</strong> Be found. Get students. Grow your institution.
        </p>
      </div>
    </>
  );
};

export default AttractStudents;